import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";

/** The Customer lifecycle verdict (GET /dashboard/api/lifecycle). */
export interface Lifecycle {
  state: "optimized" | "pass_through" | "blocked";
  reason:
    | "benchmark"
    | "paused"
    | "declined_benchmark"
    | "unpaid_conversion"
    | "free_week"
    | "payment_grace"
    | "no_card"
    | null;
  fee_waived: boolean;
  paused_since: string | null;
  benchmark_ends_at: string | null;
  /** A Benchmark set to start with the first request, not started yet. */
  benchmark_armed?: boolean;
  /** A running Free week's end (also while Paused inside it); else null. */
  free_week_ends_at: string | null;
  free_week_days_left: number | null;
  /** During a Free week: what it has saved so far and the fee waived on it,
   * computed as billing computes a fee. */
  free_week_savings?: { saved_usd: string; savings_fee_rate: string; fee_waived_usd: string };
  /** Payment grace's end, when the Customer will be Blocked; else null. */
  payment_grace_ends_at: string | null;
  payment_grace_days_left: number | null;
  /** When the Customer was Blocked; null when it is not. */
  blocked_since: string | null;
}

export const LIFECYCLE_KEY = ["lifecycle"];

export function useLifecycle() {
  return useQuery({
    queryKey: LIFECYCLE_KEY,
    queryFn: () => api.get("lifecycle").json<Lifecycle>(),
    // Another admin may pause or resume from their own browser.
    refetchOnWindowFocus: true,
  });
}

function useLifecycleAction(path: "lifecycle/pause" | "lifecycle/resume") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(path).json<Lifecycle>(),
    onSuccess: (data) => {
      qc.setQueryData(LIFECYCLE_KEY, data);
    },
  });
}

export function usePause() {
  return useLifecycleAction("lifecycle/pause");
}

export function useResume() {
  return useLifecycleAction("lifecycle/resume");
}
